// @flow

import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import {
  Popover,
  PopoverInteractionKind,
  Position,
  Button,
  InputGroup,
  Menu,
  MenuItem,
  Icon,
  Callout,
  Tag,
  Intent,
} from '@blueprintjs/core';
import AddPill from './add-pill';

export type CategoryMeta = {
  key: string,
  label: string,
  color: string,
};

export type Option = {
  id: string,
  label: string,
  categoryKey: string,
  subTypeKey?: string,
  subTypeLabel?: string,
  color?: string,
};

type Conflict = { valueLabel: string };

type Props = {
  categories: $ReadOnlyArray<CategoryMeta>,
  options: $ReadOnlyArray<Option>,
  excludeIds: $ReadOnlyArray<string>,
  singleSelectCategories: $ReadOnlyArray<string>,
  conflictsByCategory?: { [categoryKey: string]: ?Conflict },
  size?: 'sm' | 'md',
  disabled?: boolean,
  tooltip?: string,
  addLabel: string,
  categoryHeaderLabel: string,
  onPick: (option: Option) => mixed,
};

const Body = styled.div`
  width: 280px;
  max-height: 360px;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 10px;
  border-bottom: 1px solid #ebf1f5;
  gap: 6px;
  font-size: 12px;
  color: #5c7080;
`;

const Scroll = styled.div`
  overflow-y: auto;
  flex: 1;
  padding: 4px 0;
`;

const Empty = styled.div`
  padding: 14px;
  font-size: 12px;
  color: #5c7080;
  text-align: center;
  font-style: italic;
`;

const SubHeader = styled.div`
  padding: 6px 12px 2px 12px;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #5c7080;
  font-weight: 600;
`;

const findCategory = (
  categories: $ReadOnlyArray<CategoryMeta>,
  key: string,
): ?CategoryMeta => categories.find((c) => c.key === key);

const CategorizedPickerPopover = ({
  categories,
  options,
  excludeIds,
  singleSelectCategories,
  conflictsByCategory,
  size = 'sm',
  disabled,
  tooltip,
  addLabel,
  categoryHeaderLabel,
  onPick,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [pickedCategory, setPickedCategory] = useState<?string>(null);
  const [search, setSearch] = useState('');

  const excludeSet = useMemo(() => new Set(excludeIds), [excludeIds]);
  const singleSet = useMemo(
    () => new Set(singleSelectCategories),
    [singleSelectCategories],
  );

  const conflictForCategory = (key: string): ?Conflict => {
    if (!conflictsByCategory) return null;
    return conflictsByCategory[key] || null;
  };

  const valuesForCategory = useMemo(() => {
    if (!pickedCategory) return [];
    const lower = search.toLowerCase();
    const filtered = options.filter(
      (o) =>
        o.categoryKey === pickedCategory &&
        !excludeSet.has(o.id) &&
        (lower === '' || o.label.toLowerCase().includes(lower)),
    );
    const anySubType = filtered.some((o) => !!o.subTypeKey);
    if (!anySubType) {
      return filtered.slice().sort((a, b) => a.label.localeCompare(b.label));
    }
    // Preserve caller-supplied order when sub-types are present so the caller
    // controls grouping (e.g. tag options are pre-sorted by sub-type then
    // label so the rendered headers appear in a stable order).
    return filtered.slice();
  }, [pickedCategory, search, options, excludeSet]);

  const groupedValues = useMemo(() => {
    const groups: Array<{ subTypeKey: ?string, subTypeLabel: ?string, items: Option[] }> = [];
    let last = null;
    valuesForCategory.forEach((opt) => {
      const key = opt.subTypeKey || null;
      if (!last || last.subTypeKey !== key) {
        last = {
          subTypeKey: key,
          subTypeLabel: opt.subTypeLabel || opt.subTypeKey || null,
          items: [],
        };
        groups.push(last);
      }
      last.items.push(opt);
    });
    return groups;
  }, [valuesForCategory]);

  const reset = () => {
    setPickedCategory(null);
    setSearch('');
  };

  const close = () => {
    setOpen(false);
    reset();
  };

  const renderBody = () => {
    if (!pickedCategory) {
      return (
        <Body>
          <Header>{categoryHeaderLabel}</Header>
          <Menu>
            {categories.map((cat) => {
              const conflict =
                singleSet.has(cat.key) && conflictForCategory(cat.key);
              return (
                <MenuItem
                  key={cat.key}
                  text={cat.label}
                  icon={<Icon icon="tag" color={cat.color} />}
                  shouldDismissPopover={false}
                  labelElement={
                    conflict ? (
                      <Tag minimal intent={Intent.WARNING}>
                        Replaces
                      </Tag>
                    ) : null
                  }
                  onClick={() => setPickedCategory(cat.key)}
                />
              );
            })}
          </Menu>
        </Body>
      );
    }
    const cat = findCategory(categories, pickedCategory) || {
      key: pickedCategory,
      label: pickedCategory,
      color: '#5c7080',
    };
    const conflict =
      singleSet.has(pickedCategory) && conflictForCategory(pickedCategory);
    return (
      <Body>
        <Header>
          <Button
            minimal
            small
            icon="chevron-left"
            onClick={() => {
              setPickedCategory(null);
              setSearch('');
            }}
            title="Back"
          />
          <span>{cat.label}</span>
        </Header>
        {conflict && (
          <div style={{ padding: '8px 10px' }}>
            <Callout intent={Intent.PRIMARY} icon="info-sign">
              Already has <strong>{conflict.valueLabel}</strong>. Picking a
              new value will replace it.
            </Callout>
          </div>
        )}
        <div style={{ padding: '6px 8px' }}>
          <InputGroup
            small
            leftIcon="search"
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Scroll>
          {valuesForCategory.length === 0 ? (
            <Empty>No values available</Empty>
          ) : (
            <Menu>
              {groupedValues.map((g, gi) => (
                <React.Fragment key={g.subTypeKey || `__flat_${gi}`}>
                  {g.subTypeKey && (
                    <SubHeader>{g.subTypeLabel || g.subTypeKey}</SubHeader>
                  )}
                  {g.items.map((opt) => (
                    <MenuItem
                      key={opt.id}
                      text={opt.label}
                      onClick={() => {
                        onPick(opt);
                        close();
                      }}
                    />
                  ))}
                </React.Fragment>
              ))}
            </Menu>
          )}
        </Scroll>
      </Body>
    );
  };

  return (
    <Popover
      isOpen={open}
      onInteraction={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
      position={Position.BOTTOM_LEFT}
      boundary="viewport"
      interactionKind={PopoverInteractionKind.CLICK}
      content={renderBody()}
      disabled={disabled}
    >
      <AddPill
        label={addLabel}
        size={size}
        disabled={disabled}
        tooltip={disabled ? tooltip : undefined}
        onClick={() => {
          if (!disabled) setOpen(true);
        }}
      />
    </Popover>
  );
};

export default CategorizedPickerPopover;
